import { ArtistSubscriptionFragment } from '@minecraft.graphql-operations';
import Modal, { useModal } from '@minecraft.modal';
import React, { useEffect } from 'react';
import {
  BasicContainer,
  BodyTextLarge,
  BodyTextSmall,
  FlexCol,
  FlexRow,
  H1Text,
  H3Text,
  PaywallButton,
} from '@minecraft.atoms';
import IconWithErrorBoundary from '@minecraft.icon';
import { gqlSubscription } from '@minecraft.graphql-types';
import { Trans, useTranslation } from '../../I18N';
import { PaywallLocation } from '../enums/PaywallLocation';
import { BillingCodes } from '../enums/plans';
import { PlanType, usePlans } from '../usePlans';
import { priceFormatter } from '../helpers/priceFormatter';

const SESSION_STORAGE_KEY = 'specialDiscountModalOpened';

type Props = {
  showBillingPopup: (data: { location?: PaywallLocation }) => void;
  setSelectedPlan: (plan: PlanType) => void;
  subscription: ArtistSubscriptionFragment | gqlSubscription;
  artistId: string | number;
};

export const SpecialDiscountModal = ({ setSelectedPlan, showBillingPopup, subscription, artistId }: Props) => {
  const specialDiscountModalOpened = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY)) || false;
  const { t } = useTranslation();

  const { plans, currentPlan } = usePlans({ subscription, artistId, paywallLocation: PaywallLocation.GLOBAL_NAV });
  const { isOpen, open, toggleModal, close } = useModal();

  const billingPlanCodes = plans?.map((plan) => plan.billingCode);
  const discountedPlanAnnual = plans?.find((plan) => plan.billingCode === BillingCodes.premiumAnnualAt1499);
  const isCurrentPlanBasic = currentPlan?.billingCode === BillingCodes.registration;

  const originalPrice = priceFormatter({
    price: Number(discountedPlanAnnual?.strikeThroughPrice),
    currencyCode: discountedPlanAnnual?.currencyCode,
    t,
    roundingFloor: true,
  });
  const price = priceFormatter({
    price: Number(discountedPlanAnnual?.discountedPrice) / 12,
    currencyCode: discountedPlanAnnual?.currencyCode,
    t,
    roundingFloor: true,
  });
  const annualPrice = priceFormatter({
    price: Number(discountedPlanAnnual?.discountedPrice),
    currencyCode: discountedPlanAnnual?.currencyCode,
    t,
  });

  useEffect(() => {
    if (
      isCurrentPlanBasic &&
      billingPlanCodes?.some((billingCode) =>
        [BillingCodes.premiumAnnualAt1499, BillingCodes.premiumMonthDiscount15].includes(billingCode as BillingCodes)
      ) &&
      !specialDiscountModalOpened
    ) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
      open();
    }
  }, [billingPlanCodes, specialDiscountModalOpened, isOpen, open, isCurrentPlanBasic]);

  return (
    <Modal
      title=""
      handleToggleClick={toggleModal}
      isOpen={isOpen}
      isFooterHidden
      isHeaderHidden
      modalSize="small"
      data-testid="special-discount-modal"
    >
      <FlexCol justifyItems="center" alignItems="center" gap="6" w="full" mt="8" mb="4">
        <IconWithErrorBoundary name="discount-asset" />
        <H1Text>{t('common:paywall.plan.special_offer')}</H1Text>
        <FlexRow
          bgColor="discountOfferBg"
          borderRadius="md"
          className="cn_atom_p-2"
          gap="1"
          alignItems="center"
          justifyItems="center"
        >
          <IconWithErrorBoundary name="discount-tag" />
          <H1Text color="highlightDiscount">
            {t('common:paywall.plan.save_offer', { discountPercent: discountedPlanAnnual?.discountPercent })}
          </H1Text>
        </FlexRow>
        <FlexCol justifyItems="center" alignItems="center" gap="2">
          <BodyTextLarge fontWeight="medium" as="span" textWrap="balance" align="center">
            <Trans
              i18nKey="common:paywall.plan.upgrade_premium_discount"
              components={[
                <BodyTextLarge fontWeight="medium" color="disabled" className="cn_line-through" as="span" />,
                <H3Text fontWeight="bold" color="highlightDiscount" as="span" />,
              ]}
              values={{
                originalPrice,
                price,
              }}
            />
          </BodyTextLarge>
          <BodyTextSmall>
            {annualPrice} {t('common:paywall.plan.billed_yearly')}
          </BodyTextSmall>
        </FlexCol>
        <FlexCol w="full" gap="4" mdProps={{ w: '2/3' }}>
          {discountedPlanAnnual?.keyFeatures?.map((feature) => (
            <FlexRow gap="3" alignItems="start" w="full" key={feature.code}>
              <IconWithErrorBoundary name="check-circle" themeColor="discountOffer" />
              <BasicContainer w="4/5">
                <BodyTextSmall textWrap="balance">
                  <Trans i18nKey={feature.label} components={[<b />]} />
                </BodyTextSmall>
              </BasicContainer>
            </FlexRow>
          ))}
          <PaywallButton
            label={t('common:paywall.plan.discount.claim_offer')}
            color="discount"
            onClick={() => {
              showBillingPopup({ location: PaywallLocation.DISCOUNT_MODAL });
              setSelectedPlan(discountedPlanAnnual);
              close();
            }}
            full
            isSmall
          />
        </FlexCol>
      </FlexCol>
    </Modal>
  );
};
